/**
 * Buyer Hub journey state — pure derivation from corporate-record facts (U3).
 */

export type BuyerHubDestination = "home" | "spend" | "proposal" | "planning" | "reporting";

export type BuyerHubStateKey =
  | "ready_for_phase_1"
  | "phase_1_napkin_ready"
  | "proposal_in_progress"
  | "proposal_accepted"
  | "planning_done"
  | "launch_blocked"
  | "watching_campaign";

/** Facts already on the corporate / program record — no Hub-only progress store. */
export interface BuyerHubJourneyInput {
  fiveMetricsComplete: boolean;
  hasSpendFile: boolean;
  proposalAccepted: boolean;
  planningComplete: boolean;
  campaignLaunched: boolean;
  launchBlocked: boolean;
}

export interface BuyerHubJourneyState {
  stateKey: BuyerHubStateKey;
  phase: string;
  nextAction: string | null;
  owner: string;
  completedWork: string;
  visibleDestinations: readonly BuyerHubDestination[];
  showMetricsWizard: boolean;
  launchBlockedMessage: string | null;
}

const STATE_TABLE: Record<
  BuyerHubStateKey,
  Pick<BuyerHubJourneyState, "phase" | "nextAction" | "owner" | "completedWork">
> = {
  ready_for_phase_1: {
    phase: "Discover",
    nextAction: "Complete metrics wizard",
    owner: "A1",
    completedWork: "",
  },
  phase_1_napkin_ready: {
    phase: "Discover",
    nextAction: "Upload spend file",
    owner: "A1",
    completedWork: "",
  },
  proposal_in_progress: {
    phase: "Decide",
    nextAction: "View proposal / continue planning",
    owner: "A1",
    completedWork: "Spend file in",
  },
  proposal_accepted: {
    phase: "Decide",
    nextAction: "View proposal / continue planning",
    owner: "A1 / A2",
    completedWork: "Proposal accepted",
  },
  planning_done: {
    phase: "Decide",
    nextAction: null,
    owner: "A2",
    completedWork: "Planning done",
  },
  launch_blocked: {
    phase: "Decide",
    nextAction: null,
    owner: "A2",
    completedWork: "Planning done",
  },
  watching_campaign: {
    phase: "Reporting (placeholder)",
    nextAction: "Review reporting",
    owner: "A2",
    completedWork: "Campaign launched",
  },
};

const LAUNCH_BLOCKED_MESSAGE =
  "Launch is blocked. Your bank owns the next step.";

/** Derives Hub journey state from corporate-record facts. */
export function deriveBuyerHubJourneyState(input: BuyerHubJourneyInput): BuyerHubJourneyState {
  const stateKey = resolveStateKey(input);
  const row = STATE_TABLE[stateKey];

  return {
    stateKey,
    phase: row.phase,
    nextAction: stateKey === "launch_blocked" ? null : row.nextAction,
    owner: row.owner,
    completedWork: row.completedWork,
    visibleDestinations: resolveVisibleDestinations(input, stateKey),
    showMetricsWizard: stateKey === "ready_for_phase_1",
    launchBlockedMessage: stateKey === "launch_blocked" ? LAUNCH_BLOCKED_MESSAGE : null,
  };
}

function resolveStateKey(input: BuyerHubJourneyInput): BuyerHubStateKey {
  if (input.campaignLaunched) return "watching_campaign";
  if (input.launchBlocked && input.planningComplete) return "launch_blocked";
  if (input.planningComplete) return "planning_done";
  if (input.proposalAccepted) return "proposal_accepted";
  if (input.hasSpendFile) return "proposal_in_progress";
  if (input.fiveMetricsComplete) return "phase_1_napkin_ready";
  return "ready_for_phase_1";
}

function resolveVisibleDestinations(
  input: BuyerHubJourneyInput,
  stateKey: BuyerHubStateKey,
): readonly BuyerHubDestination[] {
  const destinations: BuyerHubDestination[] = ["home"];

  // R17 / D30: spend stays available after the first file until launch.
  if (!input.campaignLaunched && (input.fiveMetricsComplete || input.hasSpendFile)) {
    destinations.push("spend");
  }

  if (
    input.hasSpendFile &&
    !input.campaignLaunched &&
    stateKey !== "ready_for_phase_1" &&
    stateKey !== "phase_1_napkin_ready"
  ) {
    destinations.push("proposal", "planning");
  }

  if (input.campaignLaunched) {
    destinations.push("reporting");
  }

  return destinations;
}

/** Stable Hub path segments for sidebar destinations. */
export function buyerHubDestinationPath(
  corporateId: number,
  destination: BuyerHubDestination,
): string {
  const base = `/buyer-hub/${corporateId}`;
  switch (destination) {
    case "home":
      return base;
    case "spend":
      return `${base}/spend`;
    case "proposal":
      return `${base}/proposal`;
    case "planning":
      return `${base}/planning`;
    case "reporting":
      return `${base}/reporting`;
    default: {
      const exhaustive: never = destination;
      return exhaustive;
    }
  }
}
